import pLimit from 'p-limit'

export default class GridFetchHelper {
  constructor({ valuesUrl, customersFetchedPerBatch, uploadId, maxConcurrentFetchRequests = 5 }) {
    this.valuesUrl = valuesUrl
    this.customersFetchedPerBatch = customersFetchedPerBatch
    this.uploadId = uploadId
    this.maxConcurrentFetchRequests = maxConcurrentFetchRequests

    this.fetchAbortController = null

    this.limit = pLimit(maxConcurrentFetchRequests)
  }

  async fetchAllData(monthAsString) {
    // Abort any existing requests
    if (this.fetchAbortController) {
      this.fetchAbortController.abort()
    }

    this.fetchAbortController = new AbortController()

    try {
      const [batchResults] = await Promise.all([this.fetchRows(monthAsString)])
      // Check if any batch results are undefined (indicating aborted requests)
      if (batchResults.some(result => result === undefined)) {
        const abortError = new Error('Fetch was aborted')
        abortError.name = 'AbortError'
        throw abortError
      }

      const allData = this.concatRows(batchResults)

      return {
        rows: allData
      }
    } catch (error) {
      throw error
    }
  }

  buildQueryParams(monthAsString, params = {}) {
    const queryParams = new URLSearchParams()
    queryParams.append('start_date', monthAsString)
    queryParams.append('upload_id', this.uploadId)

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, value)
      }
    })

    return queryParams
  }

  createFetchPromise(url, queryParams, errorMessage = 'Request') {
    return fetch(url + `?${queryParams}`, {
      method: 'get',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      signal: this.fetchAbortController.signal
    })
      .then(response => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          return errorMessage.includes('Totals') ? {} : []
        }
        throw error
      })
  }

  async fetchRows(monthAsString) {
    const countQueryParams = this.buildQueryParams(monthAsString)
    const response = await this.createFetchPromise(`${this.valuesUrl}/total_count`, countQueryParams, 'Total count')
    const totalBatches = Math.ceil(response.total_count / this.customersFetchedPerBatch)
    const batchPromises = []

    const startTime = performance.now()

    for (let page = 1; page <= totalBatches; page++) {
      const queryParams = this.buildQueryParams(monthAsString, {
        page: page,
        customer_per_page: this.customersFetchedPerBatch
      })

      // Use p-limit to control concurrency for each batch fetch
      const promise = this.limit(() =>
        this.createFetchPromise(`${this.valuesUrl}/rows`, queryParams, 'Data batch').then(data => data.rows)
      )

      batchPromises.push(promise)
    }

    const results = await Promise.all(batchPromises)
    const endTime = performance.now()
    const totalTime = endTime - startTime
    const avgTime = totalTime / totalBatches
    console.log(
      `fetchRows: Total time for ${totalBatches} batches: ${totalTime.toFixed(
        2
      )} ms, average per batch: ${avgTime.toFixed(2)} ms`
    )
    return results
  }

  concatRows(batchResults) {
    const allData = []

    batchResults.forEach(rows => {
      rows.forEach(row => {
        this.normalizeDayValues(row)
      })
      allData.push(...rows)
    })

    return allData
  }

  normalizeDayValues(row) {
    for (let i = 1; i <= 31; i++) {
      const dayKey = `days_${i}`
      if (row[dayKey] !== null && row[dayKey] !== undefined) {
        row[dayKey] = parseFloat(row[dayKey])
      }
    }
    return row
  }
}
